import { useResizeObserver } from "@react-hookz/web";
import { isBrowser } from "@react-hookz/web/cjs/util/const";
import {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import throttle from "lodash.throttle";

const lastMeasurementsKeyMap = new Map<string, DOMRect>();

function getBBoxOrDefault(div: null | HTMLElement): DOMRect {
  const defaultMeasure = { width: 0, height: 0 };

  if (!isBrowser) {
    return defaultMeasure as DOMRect; // FIXME: prob not a good idea but since it will just be undefined values will likely be fine
  }

  const bbox = div?.getBoundingClientRect();

  if (!bbox) {
    return defaultMeasure as DOMRect; // FIXME: prob not a good idea but since it will just be undefined values will likely be fine
  }

  return bbox;
}

export function useRefMeasure<T extends HTMLElement>({
  key,
  ref,
  enabled = true,
}: {
  key?: string;
  ref: MutableRefObject<T | null>;
  enabled?: boolean;
}): DOMRect {
  const [measurements, setMeasurements] = useState<DOMRect>(
    key
      ? lastMeasurementsKeyMap.get(key) ?? getBBoxOrDefault(ref.current)
      : getBBoxOrDefault(ref.current)
  );

  const measurementsUpdater = useCallback(
    (bbox: DOMRect) => {
      setMeasurements((prev) => {
        if (
          prev.width === bbox.width &&
          prev.height === bbox.height &&
          prev.bottom === bbox.bottom &&
          prev.top === bbox.top &&
          prev.left === bbox.left &&
          prev.right === bbox.right
        ) {
          return prev;
        }

        return bbox;
      });

      // If a key is present it means to store the measurements
      // it is used to render the same component across different parent components
      // like a failed screen vs a game screen
      // don't store them if they are 0 because that will add a flash that this is avoiding
      if (key) {
        if (bbox.width > 0 && bbox.height > 0) {
          lastMeasurementsKeyMap.set(key, bbox);
        }
      }
    },
    [key]
  );

  useResizeObserver(
    ref,
    () => {
      measurementsUpdater(getBBoxOrDefault(ref.current));
    },
    enabled
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const scroll = throttle(() => {
      measurementsUpdater(getBBoxOrDefault(ref.current));
    }, 500);

    const resize = throttle(() => {
      measurementsUpdater(getBBoxOrDefault(ref.current));
    }, 500);

    window.addEventListener("scroll", scroll);
    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("scroll", scroll);
      window.removeEventListener("resize", resize);
    };
  }, [enabled, measurementsUpdater, ref]);

  return measurements;
}

/**
 * Fixes the height being broken on normal useMeasure from react-hookz
 */
export default function useMeasure<T extends HTMLElement>({
  key,
  enabled = true,
}: {
  key?: string;
  enabled?: boolean;
} = {}): [DOMRect, RefObject<T>] {
  const ref = useRef<T | null>(null);
  const measurements = useRefMeasure({ ref, enabled, key });

  return [measurements, ref];
}
